import { FormControl } from "@chakra-ui/form-control";
import { Input } from "@chakra-ui/input";
import { Box, Text } from "@chakra-ui/layout";
import { Avatar } from "@chakra-ui/avatar";
import "./styles.css";
import {
  IconButton,
  Spinner,
  useToast,
  Image,
  Button,
  Textarea,
} from "@chakra-ui/react";
import { getSender, getSenderFull } from "../config/ChatLogics";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  ArrowBackIcon,
  AttachmentIcon,
  CloseIcon,
} from "@chakra-ui/icons";
import ProfileModal from "./miscellaneous/ProfileModal";
import ScrollableChat from "./ScrollableChat";

import io from "socket.io-client";
import UpdateGroupChatModal from "./miscellaneous/UpdateGroupChatModal";
import { ChatState } from "../Context/ChatProvider";
import {
  SendIcon,
  EaselArt,
  Ornament,
  PaletteIcon,
  BookmarkIcon,
} from "./ui/Icons";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import notifySoundFile from "../assets/notify.mp3";

// на телефоне Enter должен переносить строку, а не отправлять
const isTouchDevice =
  typeof window !== "undefined" &&
  window.matchMedia("(hover: none) and (pointer: coarse)").matches;

// негромкий «поп» при входящем сообщении
const notifySound = new Audio(notifySoundFile);
notifySound.volume = 0.35;
const playNotify = () => {
  notifySound.currentTime = 0;
  notifySound.play().catch(() => {}); // браузер может запретить до первого клика
};

const ENDPOINT =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5001"
    : window.location.origin;
var socket, selectedChatCompare;

const SingleChat = ({ fetchAgain, setFetchAgain }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [typing, setTyping] = useState(false);
  const [istyping, setIsTyping] = useState(false);
  const [typingName, setTypingName] = useState(null);
  const fileRef = useRef(null);
  const textRef = useRef(null);
  const toast = useToast();

  // поле ввода растёт под текст и не даёт чату прыгать
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    el.style.height = "46px"; // пустое поле — всегда одна строка
    if (newMessage) {
      el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
    }
  }, [newMessage]);

  const { selectedChat, setSelectedChat, user, notification, setNotification } =
    ChatState();

  const config = { headers: { Authorization: `Bearer ${user.token}` } };

  const iAmManager =
    selectedChat?.isGroupChat &&
    (selectedChat.groupAdmin?._id === user._id ||
      (selectedChat.groupAdmins || []).includes(user._id));

  const canWrite =
    !selectedChat?.isGroupChat ||
    selectedChat.membersCanWrite !== false ||
    iAmManager;

  const fetchMessages = async () => {
    if (!selectedChat) return;

    try {
      setLoading(true);

      const { data } = await axios.get(
        `/api/message/${selectedChat._id}`,
        config
      );
      setMessages(data);
      setLoading(false);

      socket.emit("join chat", selectedChat._id);
    } catch (error) {
      toast({
        title: "Ошибка",
        description: "Не удалось загрузить сообщения",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  const startEdit = (m) => {
    setEditingMessage(m);
    setReplyTo(null);
    setNewMessage(m.content);
    setAttachment(null);
    setShowEmoji(false);
  };

  const startReply = (m) => {
    setReplyTo(m);
    setEditingMessage(null);
    setShowEmoji(false);
  };

  const cancelEdit = () => {
    setEditingMessage(null);
    setNewMessage("");
  };

  const sendMessage = async () => {
    // режим редактирования
    if (editingMessage) {
      if (!newMessage.trim()) return;
      try {
        const { data } = await axios.put(
          `/api/message/${editingMessage._id}`,
          { content: newMessage },
          config
        );
        setMessages((prev) =>
          prev.map((m) =>
            m._id === data._id ? { ...m, content: data.content, edited: true } : m
          )
        );
        socket.emit("message edited", {
          message: data,
          chat: selectedChat,
          senderId: user._id,
        });
        cancelEdit();
        setFetchAgain(!fetchAgain);
      } catch (error) {
        toast({
          title: "Не удалось отредактировать",
          description: error.response?.data?.message,
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "bottom",
        });
      }
      return;
    }

    if (!newMessage && !attachment) return;
    socket.emit("stop typing", { room: selectedChat._id });

    // оптимистичная отправка: сообщение появляется сразу
    const sentText = newMessage;
    const sentAttachment = attachment;
    const sentReplyTo = replyTo;
    const tempId = `tmp-${Date.now()}`;
    const optimistic = {
      _id: tempId,
      sender: { _id: user._id, name: user.name, pic: user.pic },
      content: sentText,
      attachment: sentAttachment || undefined,
      replyTo: sentReplyTo || undefined,
      createdAt: new Date().toISOString(),
      pending: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    setNewMessage("");
    setAttachment(null);
    setReplyTo(null);
    setMentionQuery(null);
    setShowEmoji(false);

    try {
      const body = {
        content: sentText,
        chatId: selectedChat._id,
        attachment: sentAttachment || undefined,
        replyTo: sentReplyTo?._id,
      };
      const { data } = await axios.post("/api/message", body, {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
      });
      socket.emit("new message", data);
      setMessages((prev) => prev.map((m) => (m._id === tempId ? data : m)));
      setFetchAgain(!fetchAgain);
    } catch (error) {
      // не ушло — убираем призрак и возвращаем текст в поле
      setMessages((prev) => prev.filter((m) => m._id !== tempId));
      setNewMessage(sentText);
      setAttachment(sentAttachment);
      setReplyTo(sentReplyTo);
      toast({
        title: "Не удалось отправить",
        description: error.response?.data?.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast({
        title: "Файл больше 15 МБ",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    try {
      setUploading(true);
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post("/api/upload", form, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setAttachment(data);
    } catch (error) {
      toast({
        title: "Не удалось загрузить файл",
        description: error.response?.data?.message,
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setUploading(false);
    }
  };

  const deleteMessage = async () => {
    const m = deleteTarget;
    if (!m) return;
    setDeleteTarget(null);
    try {
      await axios.delete(`/api/message/${m._id}`, config);
      setMessages((prev) => prev.filter((x) => x._id !== m._id));
      socket.emit("message deleted", {
        messageId: m._id,
        chat: selectedChat,
        senderId: user._id,
      });
      setFetchAgain(!fetchAgain);
    } catch (error) {
      toast({
        title: "Не удалось удалить",
        description: error.response?.data?.message,
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  const canDeleteMessage = (m) =>
    m.sender._id === user._id ||
    (selectedChat?.isGroupChat && iAmManager);

  useEffect(() => {
    socket = io(ENDPOINT);
    socket.emit("setup", user);
    socket.on("connected", () => setSocketConnected(true));
    socket.on("typing", (name) => {
      setTypingName(name || null);
      setIsTyping(true);
    });
    socket.on("stop typing", () => setIsTyping(false));

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchMessages();

    selectedChatCompare = selectedChat;
    // eslint-disable-next-line
  }, [selectedChat]);

  useEffect(() => {
    const onMessage = (newMessageRecieved) => {
      if (
        !selectedChatCompare ||
        selectedChatCompare._id !== newMessageRecieved.chat._id
      ) {
        if (!notification.includes(newMessageRecieved)) {
          setNotification([newMessageRecieved, ...notification]);
          setFetchAgain(!fetchAgain);
          playNotify();
        }
      } else {
        setMessages((prev) => [...prev, newMessageRecieved]);
        playNotify();
      }
    };
    const onRemoved = ({ messageId, chatId }) => {
      if (selectedChatCompare && selectedChatCompare._id === chatId) {
        setMessages((prev) => prev.filter((m) => m._id !== messageId));
      }
    };
    const onUpdated = (updatedMessage) => {
      if (
        selectedChatCompare &&
        selectedChatCompare._id === updatedMessage.chat._id
      ) {
        setMessages((prev) =>
          prev.map((m) =>
            m._id === updatedMessage._id
              ? { ...m, content: updatedMessage.content, edited: true }
              : m
          )
        );
      }
    };
    const onChatRemoved = ({ chatId }) => {
      if (selectedChatCompare && selectedChatCompare._id === chatId) {
        setSelectedChat(undefined);
        toast({
          title: "Беседу удалил её создатель",
          status: "info",
          duration: 4000,
          isClosable: true,
          position: "bottom",
        });
      }
      setFetchAgain((prev) => !prev);
    };
    socket.on("message recieved", onMessage);
    socket.on("message removed", onRemoved);
    socket.on("message updated", onUpdated);
    socket.on("chat removed", onChatRemoved);
    return () => {
      socket.off("message recieved", onMessage);
      socket.off("message removed", onRemoved);
      socket.off("message updated", onUpdated);
      socket.off("chat removed", onChatRemoved);
    };
  });

  const typingHandler = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    // автокомплит упоминаний: @имя в конце строки
    if (selectedChat?.isGroupChat) {
      const m = value.match(/(?:^|\s)@([\wа-яё_-]*)$/iu);
      setMentionQuery(m ? m[1] : null);
    }

    if (!socketConnected) return;

    if (!typing) {
      setTyping(true);
      socket.emit("typing", { room: selectedChat._id, name: user.name });
    }
    let lastTypingTime = new Date().getTime();
    var timerLength = 3000;
    setTimeout(() => {
      var timeNow = new Date().getTime();
      var timeDiff = timeNow - lastTypingTime;
      if (timeDiff >= timerLength && typing) {
        socket.emit("stop typing", { room: selectedChat._id });
        setTyping(false);
      }
    }, timerLength);
  };

  const insertMention = (name) => {
    setNewMessage((prev) =>
      prev.replace(/@([\wа-яё_-]*)$/iu, `@${name} `)
    );
    setMentionQuery(null);
  };

  const mentionCandidates =
    mentionQuery !== null && selectedChat?.isGroupChat
      ? selectedChat.users
          .filter(
            (u) =>
              u._id !== user._id &&
              u.name.toLowerCase().includes(mentionQuery.toLowerCase())
          )
          .slice(0, 5)
      : [];

  const otherUser =
    selectedChat && !selectedChat.isGroupChat && !selectedChat.isSaved
      ? getSenderFull(user, selectedChat.users)
      : null;

  return (
    <>
      {selectedChat ? (
        <>
          <Box
            pb={3}
            px={1}
            w="100%"
            d="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box d="flex" alignItems="center" minW={0}>
              <IconButton
                d={{ base: "flex", md: "none" }}
                icon={<ArrowBackIcon />}
                borderRadius="12px"
                mr={2}
                onClick={() => setSelectedChat("")}
              />
              {selectedChat.isSaved ? (
                <Box
                  w="40px"
                  h="40px"
                  minW="40px"
                  borderRadius="full"
                  bg="#8D414B"
                  d="flex"
                  alignItems="center"
                  justifyContent="center"
                  mr={3}
                >
                  <BookmarkIcon size={17} color="#F2DFB6" />
                </Box>
              ) : !selectedChat.isGroupChat ? (
                <Avatar
                  size="sm"
                  w="40px"
                  h="40px"
                  mr={3}
                  name={otherUser?.name}
                  src={otherUser?.pic}
                />
              ) : (
                <Box
                  w="40px"
                  h="40px"
                  minW="40px"
                  borderRadius="full"
                  bg="#243D27"
                  d="flex"
                  alignItems="center"
                  justifyContent="center"
                  mr={3}
                >
                  <PaletteIcon size={21} color="#F2DFB6" />
                </Box>
              )}
              <Box minW={0}>
                <Text
                  fontSize={{ base: "19px", md: "21px" }}
                  fontFamily="'Prata', serif"
                  color="#243D27"
                  lineHeight="1.2"
                  isTruncated
                >
                  {selectedChat.isSaved
                    ? "Избранное"
                    : !selectedChat.isGroupChat
                    ? getSender(user, selectedChat.users)
                    : selectedChat.chatName}
                </Text>
                <Text
                  fontSize="12px"
                  color={istyping ? "#8D414B" : "rgba(36,61,39,0.6)"}
                  h="18px"
                >
                  {selectedChat.isSaved
                    ? "заметки и файлы для себя"
                    : istyping
                    ? `${
                        selectedChat.isGroupChat && typingName
                          ? typingName + " "
                          : ""
                      }печатает…`
                    : selectedChat.isGroupChat
                    ? `участников: ${selectedChat.users.length}`
                    : ""}
                </Text>
              </Box>
            </Box>
            {messages &&
              !selectedChat.isSaved &&
              (!selectedChat.isGroupChat ? (
                <ProfileModal user={otherUser} />
              ) : (
                <UpdateGroupChatModal
                  fetchMessages={fetchMessages}
                  fetchAgain={fetchAgain}
                  setFetchAgain={setFetchAgain}
                  onDeleted={(users) => {
                    socket.emit("chat deleted", {
                      chatId: selectedChat._id,
                      users,
                      senderId: user._id,
                    });
                    setSelectedChat(undefined);
                    setFetchAgain(!fetchAgain);
                  }}
                />
              ))}
          </Box>
          <Box
            d="flex"
            flexDir="column"
            justifyContent="flex-end"
            p={3}
            bg="#F2DFB6"
            backgroundImage="radial-gradient(ellipse 90% 60% at 50% 0%, rgba(253, 247, 233, 0.5) 0%, rgba(242, 223, 182, 0) 60%)"
            w="100%"
            h="100%"
            borderRadius="14px"
            border="1px solid #EAD9AC"
            overflowY="hidden"
          >
            {loading ? (
              <Spinner
                size="xl"
                w={16}
                h={16}
                thickness="3px"
                alignSelf="center"
                margin="auto"
                color="#8D414B"
              />
            ) : (
              <div className="messages">
                <ScrollableChat
                  messages={messages}
                  canDelete={canDeleteMessage}
                  onDelete={(m) => setDeleteTarget(m)}
                  onEdit={startEdit}
                  onReply={startReply}
                />
              </div>
            )}

            <FormControl id="message-input" mt={3} position="relative">
              {/* на компьютере подсказываем, как сделать новый абзац */}
              {!isTouchDevice && newMessage && !showEmoji && (
                <Text
                  position="absolute"
                  bottom="100%"
                  right="2px"
                  mb="6px"
                  fontSize="11px"
                  color="rgba(36, 61, 39, 0.5)"
                  bg="rgba(253, 247, 233, 0.85)"
                  borderRadius="8px"
                  px={2}
                  pointerEvents="none"
                >
                  Enter — отправить, Shift + Enter — новая строка
                </Text>
              )}

              {/* подсказка упоминаний */}
              {mentionCandidates.length > 0 && (
                <Box
                  position="absolute"
                  bottom="100%"
                  left="0"
                  right="0"
                  mb={2}
                  bg="#FBF0D9"
                  border="1px solid #D3BC8D"
                  borderRadius="12px"
                  boxShadow="0 14px 34px -14px rgba(36, 61, 39, 0.4)"
                  overflow="hidden"
                  zIndex={10}
                  w="min(220px, 100%)"
                >
                  {mentionCandidates.map((u) => (
                    <Box
                      key={u._id}
                      d="flex"
                      alignItems="center"
                      px={3}
                      py={2}
                      cursor="pointer"
                      _hover={{ bg: "#F2DFB6" }}
                      onClick={() => insertMention(u.name)}
                    >
                      <Avatar size="xs" name={u.name} src={u.pic} mr={2} />
                      <Text fontSize="sm">{u.name}</Text>
                    </Box>
                  ))}
                </Box>
              )}

              {/* плашка ответа */}
              {replyTo && (
                <Box
                  d="flex"
                  alignItems="center"
                  bg="#F6E8C6"
                  border="1px solid #E4D2A6"
                  borderLeft="3px solid #8D414B"
                  borderRadius="12px"
                  px={3}
                  py={2}
                  mb={2}
                >
                  <Box flex="1" minW={0}>
                    <Text fontSize="xs" color="#8D414B" fontWeight={600}>
                      Ответ: {replyTo.sender?.name}
                    </Text>
                    <Text fontSize="xs" isTruncated opacity={0.7}>
                      {replyTo.content || "вложение"}
                    </Text>
                  </Box>
                  <IconButton
                    aria-label="Отменить ответ"
                    icon={<CloseIcon boxSize="9px" />}
                    size="xs"
                    variant="ghost"
                    colorScheme="burgundy"
                    onClick={() => setReplyTo(null)}
                  />
                </Box>
              )}

              {/* плашка режима редактирования */}
              {editingMessage && (
                <Box
                  d="flex"
                  alignItems="center"
                  bg="#F6E8C6"
                  border="1px solid #E4D2A6"
                  borderRadius="12px"
                  px={3}
                  py={2}
                  mb={2}
                >
                  <Text fontSize="xs" color="#8D414B" fontWeight={600} mr={2}>
                    Редактирование
                  </Text>
                  <Text fontSize="xs" flex="1" isTruncated opacity={0.7}>
                    {editingMessage.content}
                  </Text>
                  <IconButton
                    aria-label="Отменить"
                    icon={<CloseIcon boxSize="9px" />}
                    size="xs"
                    variant="ghost"
                    colorScheme="burgundy"
                    onClick={cancelEdit}
                  />
                </Box>
              )}

              {/* панель эмодзи */}
              {showEmoji && (
                <Box
                  position="absolute"
                  bottom="100%"
                  right="0"
                  left="0"
                  mb={2}
                  mx="auto"
                  bg="#FBF0D9"
                  border="1px solid #D3BC8D"
                  borderRadius="14px"
                  boxShadow="0 14px 34px -14px rgba(36, 61, 39, 0.4)"
                  p={2}
                  zIndex={10}
                  w="min(264px, 100%)"
                >
                  {[
                    "😀","😁","😂","🤣","😊","😍","😘","😎",
                    "🤔","🙃","😜","🥰","😇","🥲","😭","😱",
                    "🙈","🤗","🤩","😴","🤝","👋","👍","👎",
                    "👏","🙏","💪","🔥","✨","💛","❤️","💔",
                    "🎨","🖌️","🖼️","✏️","📸","🌿","🌸","☕",
                  ].map((e) => (
                    <button
                      key={e}
                      onClick={() => {
                        setNewMessage((prev) => prev + e);
                        setShowEmoji(false);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "22px",
                        padding: "4px",
                        cursor: "pointer",
                        lineHeight: 1,
                      }}
                    >
                      {e}
                    </button>
                  ))}
                </Box>
              )}

              {/* превью вложения */}
              {attachment && (
                <Box
                  d="flex"
                  alignItems="center"
                  bg="#FDF7E9"
                  border="1px solid #E4D2A6"
                  borderRadius="12px"
                  px={3}
                  py={2}
                  mb={2}
                  maxW="360px"
                >
                  {attachment.kind === "image" ? (
                    <Image
                      src={attachment.url}
                      alt=""
                      boxSize="42px"
                      objectFit="cover"
                      borderRadius="8px"
                      mr={2}
                    />
                  ) : (
                    <AttachmentIcon color="#8D414B" mr={2} />
                  )}
                  <Text fontSize="xs" flex="1" isTruncated>
                    {attachment.name}
                  </Text>
                  <IconButton
                    aria-label="Убрать вложение"
                    icon={<CloseIcon boxSize="9px" />}
                    size="xs"
                    variant="ghost"
                    colorScheme="burgundy"
                    onClick={() => setAttachment(null)}
                  />
                </Box>
              )}

              {canWrite ? (
                <Box d="flex" alignItems="center">
                  <IconButton
                    aria-label="Прикрепить файл"
                    icon={<AttachmentIcon />}
                    isLoading={uploading}
                    onClick={() => fileRef.current?.click()}
                    mr={2}
                    w="46px"
                    h="46px"
                    minW="46px"
                    borderRadius="full"
                    bg="#F6E8C6"
                    color="#243D27"
                    _hover={{ bg: "#EAD9AC" }}
                  />
                  <input
                    ref={fileRef}
                    type="file"
                    hidden
                    onChange={(e) => {
                      uploadFile(e.target.files[0]);
                      e.target.value = "";
                    }}
                  />
                  <IconButton
                    aria-label="Эмодзи"
                    icon={
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" stroke="#243D27" strokeWidth="1.6" />
                        <circle cx="9" cy="10" r="1.2" fill="#243D27" />
                        <circle cx="15" cy="10" r="1.2" fill="#243D27" />
                        <path d="M8.5 14.5c1 1.2 2.17 1.8 3.5 1.8s2.5-.6 3.5-1.8" stroke="#8D414B" strokeWidth="1.6" strokeLinecap="round" />
                      </svg>
                    }
                    onClick={() => setShowEmoji((v) => !v)}
                    mr={2}
                    w="46px"
                    h="46px"
                    minW="46px"
                    borderRadius="full"
                    bg={showEmoji ? "#EAD9AC" : "#F6E8C6"}
                    _hover={{ bg: "#EAD9AC" }}
                  />
                  <Textarea
                    ref={textRef}
                    placeholder={
                      editingMessage
                        ? "Новый текст сообщения…"
                        : "Привет! Как твои дела?"
                    }
                    value={newMessage}
                    onChange={typingHandler}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setMentionQuery(null);
                        setShowEmoji(false);
                        if (editingMessage) cancelEdit();
                        return;
                      }
                      if (e.key !== "Enter") return;
                      // с телефона и по Shift+Enter — перенос строки,
                      // на компьютере обычный Enter отправляет
                      if (isTouchDevice || e.shiftKey) return;
                      e.preventDefault();
                      sendMessage();
                    }}
                    onPaste={(e) => {
                      const item = [...(e.clipboardData?.items || [])].find(
                        (i) => i.type.startsWith("image/")
                      );
                      if (item) {
                        e.preventDefault();
                        uploadFile(item.getAsFile());
                      }
                    }}
                    rows={1}
                    resize="none"
                    overflowY="auto"
                    minH="46px"
                    maxH="132px"
                    py="11px"
                    px={5}
                    bg="#FDF7E9"
                    borderRadius="23px"
                    border="1px solid"
                    borderColor="#D3BC8D"
                    _hover={{ bg: "#FFFBF0", borderColor: "#B89F6E" }}
                    _focus={{
                      bg: "#FFFBF0",
                      borderColor: "#8D414B",
                      boxShadow: "0 0 0 3px rgba(141, 65, 75, 0.18)",
                    }}
                  />
                  <IconButton
                    aria-label="Отправить"
                    title={
                      isTouchDevice
                        ? "Отправить"
                        : "Отправить (Enter). Новая строка — Shift + Enter"
                    }
                    icon={<SendIcon size={17} />}
                    onClick={sendMessage}
                    ml={2}
                    w="46px"
                    h="46px"
                    minW="46px"
                    borderRadius="full"
                    bg="#8D414B"
                    color="#FBF0D9"
                    boxShadow="0 6px 16px -6px rgba(141, 65, 75, 0.6)"
                    transition="all 0.18s ease"
                    _hover={{ bg: "#7A3841", transform: "translateY(-1px)" }}
                    _active={{ bg: "#632D35", transform: "none" }}
                  />
                </Box>
              ) : (
                <Box
                  textAlign="center"
                  bg="#F6E8C6"
                  border="1px dashed #D3BC8D"
                  borderRadius="full"
                  py={3}
                >
                  <Text fontSize="sm" color="rgba(36,61,39,0.65)">
                    В этой беседе писать могут только админы
                  </Text>
                </Box>
              )}
            </FormControl>
          </Box>
        </>
      ) : (
        <Box
          d="flex"
          flexDir="column"
          alignItems="center"
          justifyContent="center"
          h="100%"
        >
          <EaselArt width={150} />
          <Text
            fontFamily="'Prata', serif"
            fontSize="21px"
            color="#243D27"
            mt={6}
          >
            Мастерская ждёт
          </Text>
          <Text fontSize="15px" color="rgba(36, 61, 39, 0.6)" mt={1} mb={4}>
            Выберите беседу слева, чтобы начать общение
          </Text>
          <Ornament color="#8D414B" opacity={0.5} width={120} />
        </Box>
      )}

      {/* подтверждение удаления сообщения */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        isCentered
        size="sm"
      >
        <ModalOverlay bg="rgba(20, 35, 23, 0.45)" />
        <ModalContent>
          <ModalHeader
            fontSize="20px"
            fontFamily="'Prata', serif"
            color="#243D27"
            pb={1}
          >
            Удалить сообщение?
          </ModalHeader>
          <ModalBody py={2}>
            <Text
              fontSize="sm"
              opacity={0.7}
              isTruncated
              bg="#F6E8C6"
              borderRadius="10px"
              px={3}
              py={2}
            >
              {deleteTarget?.content || "вложение"}
            </Text>
            <Text fontSize="xs" opacity={0.55} mt={2}>
              Сообщение пропадёт у всех участников. Это действие нельзя
              отменить.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              size="sm"
              variant="ghost"
              colorScheme="pine"
              mr={2}
              onClick={() => setDeleteTarget(null)}
            >
              Отмена
            </Button>
            <Button
              size="sm"
              colorScheme="burgundy"
              onClick={deleteMessage}
              _hover={{ bg: "#7A3841" }}
            >
              Удалить
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SingleChat;
